import { SESSION_REFUSAL } from '@classgo/shared';
import { useToast } from '@classgo/ui';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

import { useKitT } from '../i18n';
import type { KitMessageKey, TranslateOf } from '../i18n/types';
import { ApiError } from './client';
import { createQueryClient } from './query-client';
import type { ApiClient } from './types';

const ApiContext = createContext<ApiClient | null>(null);

/**
 * Hook the toast system into the query cache: any failed query shows the envelope's message,
 * except a 401 (the router is already sending the person to login).
 */
const ApiErrorToasts = ({ client }: { client: QueryClient }) => {
  const toast = useToast();
  const t = useKitT();
  useEffect(
    () =>
      client.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'error') return;
        const error = event.action.error;
        if (
          error instanceof ApiError &&
          error.code !== 'UNAUTHORIZED' &&
          error.code !== 'UNAVAILABLE'
        ) {
          toast.show({
            tone: 'error',
            title: t(`api.error.${error.code}`),
            description: error.message,
          });
        }
      }),
    [client, toast, t],
  );
  return null;
};

/**
 * Provides the API client and the query client, with errors mapped to toasts.
 */
export const ApiProvider = ({
  client,
  queryClient,
  children,
}: {
  client: ApiClient;
  queryClient?: QueryClient;
  children: ReactNode;
}) => {
  const [queries] = useState(() => queryClient ?? createQueryClient());
  return (
    <ApiContext.Provider value={client}>
      <QueryClientProvider client={queries}>
        <ApiErrorToasts client={queries} />
        {children}
      </QueryClientProvider>
    </ApiContext.Provider>
  );
};

/**
 * The API client.
 *
 * @returns The client.
 */
export const useApi = (): ApiClient => {
  const client = useContext(ApiContext);
  if (!client) throw new Error('useApi needs an ApiProvider');
  return client;
};

/**
 * The message a toast shows for an API or auth error.
 *
 * @param {Translate} t -- The translate function.
 * @param {unknown} error -- Whatever was thrown.
 * @returns The localized message.
 */
export const describeError = (t: TranslateOf<KitMessageKey>, error: unknown): string => {
  if (!(error instanceof ApiError)) {
    return t('api.error.UNKNOWN');
  }
  if (error.code === 'FORBIDDEN' && error.message === SESSION_REFUSAL.tenantSuspended) {
    return t('session.suspended.body');
  }
  if (error.code === 'FORBIDDEN' && error.message === SESSION_REFUSAL.userDisabled) {
    return t('session.disabled.body');
  }
  return t(`api.error.${error.code}`);
};
